import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { useEffect, useMemo, useState } from "react";
import { EmptySelection } from "../components/assign/EmptySelection";
import { useLoadData } from "../hooks/assign/useLoadData";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import { EmptyRotations } from "../components/assign/EmptyRotations";
import { EmptyPreferences } from "../components/assign/EmptyPreferences";
import { HStack } from "@/components/layout/HStack";
import { Button } from "@/components/ui/Button/Button";
import { MousePointerClick, SaveAll } from "lucide-react";
import { AssignPreferencesTable } from "../components/assign/AssignDataTable";
import { useAssign } from "../hooks/assign/useAssign";
import { useSearchParams } from "react-router-dom";

const AdminAssign = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<{ rotations: any[]; sessions: any[] }>({
    rotations: [],
    sessions: [],
  });
  const [preferences, setPreferences] = useState<{
    searched: boolean;
    preferences: any[];
    explorations: any[];
    students: any[];
  }>({ searched: false, preferences: [], explorations: [], students: [] });
  const [assignments, setAssignments] = useState<
    {
      preference: string;
      exploration: string;
    }[]
  >([]);

  const selected = useMemo(() => {
    const rotation = searchParams.get("rot") ?? undefined;
    const session = searchParams.get("sesh") ?? undefined;
    return { rotation, session };
  }, [searchParams]);

  const setSelected = (next: { rotation?: string; session?: string }) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      if (next.rotation) sp.set("rot", next.rotation);
      else sp.delete("rot");

      if (next.session) sp.set("sesh", next.session);
      else sp.delete("sesh");

      return sp;
    });
  };

  const { loadSessions, loadPreferences, loading } = useLoadData();

  useEffect(() => {
    loadSessions(setData);
  }, []);

  useEffect(() => {
    if (!selected.session) return;

    if (loading.preferences) return;
    if (preferences.searched) return;

    loadPreferences(selected.session, setPreferences);
  }, [
    selected.session,
    loading.preferences,
    preferences.searched,
    loadPreferences,
  ]);

  const getPreferences = async () => {
    if (!selected.session) return;
    await loadPreferences(selected.session, setPreferences);
  };

  const resetState = () => {
    setSelected({ rotation: undefined, session: undefined });
    setPreferences({
      searched: false,
      preferences: [],
      explorations: [],
      students: [],
    });
  };

  const { assignSingle, assignBulk, loading: assignLoading } = useAssign();

  return (
    <Dashboard activePage="/assign">
      <HStack justify="space-between" wrap>
        <VStack>
          <h2
            style={{
              fontSize: "var(--text-2xl)",
              lineHeight: "var(--line-height-2xl)",
              fontWeight: "var(--font-bold)",
            }}
          >
            Assign Preferences
          </h2>
          <p style={{ color: "var(--muted-foreground)" }}>
            Manually assign student preferences for a session.
          </p>
        </VStack>
        {preferences.preferences.length ? (
          <HStack>
            <Button variant="outline" onClick={resetState}>
              <MousePointerClick />
              Select New Session
            </Button>
            <Button onClick={() => assignBulk(assignments)}>
              <SaveAll />
              Save All Assignments
            </Button>
          </HStack>
        ) : (
          <></>
        )}
      </HStack>
      {loading.sessions ? (
        <Skeleton style={{ width: "100%", height: "100%" }} />
      ) : !data.rotations.length ? (
        <EmptyRotations />
      ) : !preferences.preferences.length && !preferences.searched ? (
        <EmptySelection
          data={data}
          selected={selected}
          setSelected={setSelected}
          getPreferences={getPreferences}
        />
      ) : !preferences.preferences.length ? (
        <EmptyPreferences
          data={data}
          selected={selected}
          setSelected={setSelected}
          getPreferences={getPreferences}
        />
      ) : (
        <AssignPreferencesTable
          data={{
            preferences: preferences.preferences,
            explorations: preferences.explorations,
            students: preferences.students,
          }}
          assignments={assignments}
          setAssignments={setAssignments}
          assign={{ assignSingle, loading: assignLoading }}
        />
      )}
    </Dashboard>
  );
};

export default AdminAssign;
